import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Flex,
  Input,
  Button,
  Icon,
  Collapse,
  IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, CopyIcon, EditIcon, CheckIcon } from '@chakra-ui/icons';
import { ff } from '../../../../../../theme';
import { BASE_HOST } from '../../constants';

export interface CreatorLinkSectionProps {
  slug: string;
  onSlugChange: (value: string) => void;
  onMarkSlugTouched?: () => void;
  slugInputLooksLikePlaceholder: boolean;
  onCopy: () => void;
  isWhereOpen: boolean;
  onToggleWhere: () => void;
  /** Persist path; return true to leave edit mode (mirrors public bio save). */
  onSaveCreatorLink?: () => boolean | Promise<boolean>;
  isSavingCreatorLink?: boolean;
}

const CreatorLinkSection: React.FC<CreatorLinkSectionProps> = ({
  slug,
  onSlugChange,
  onMarkSlugTouched,
  slugInputLooksLikePlaceholder,
  onCopy,
  isWhereOpen,
  onToggleWhere,
  onSaveCreatorLink,
  isSavingCreatorLink = false,
}) => {
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditingSlug) {
      return;
    }
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isEditingSlug]);

  const handleSave = async () => {
    if (!onSaveCreatorLink) {
      setIsEditingSlug(false);
      return;
    }
    const ok = await onSaveCreatorLink();
    if (ok) {
      setIsEditingSlug(false);
    }
  };

  return (
    <Box as="section" aria-label="Creator link">
      <Text as="h3" textStyle="eyebrow" color={ff.text2} mb="10px">
        Creator link
      </Text>

      <Box
        border="1px solid"
        borderColor={ff.line}
        borderRadius="20px"
        overflow="hidden"
        bg="white"
      >
        {isEditingSlug && onSaveCreatorLink ? (
          <Flex
            h="60px"
            pl="16px"
            pr="8px"
            align="center"
            gap={2}
            bg="white"
            borderTopRadius="20px"
          >
              <Text
                as="span"
                fontSize="16px"
                lineHeight="24px"
                color={ff.muted}
                flexShrink={0}
                userSelect="none"
              >
                {BASE_HOST}
              </Text>
              <Input
                ref={inputRef}
                value={slug}
                onChange={(e) => {
                  onMarkSlugTouched?.();
                  onSlugChange(e.target.value.replace(/^\s+|\s+$/g, '').replace(/^\//, ''));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleSave();
                  }
                }}
                fontSize="16px"
                lineHeight="24px"
                fontWeight="400"
                color={slugInputLooksLikePlaceholder ? ff.faint : ff.ink}
                border="none"
                p={0}
                h="24px"
                _focusVisible={{ boxShadow: 'none' }}
                _placeholder={{ color: ff.faint }}
                placeholder="yourname"
                pl="0"
                pr={1}
                flex={1}
                minW={0}
                variant="unstyled"
                aria-label="Edit local creators profile path"
              />
            <IconButton
              type="button"
              aria-label="Save creator link and stop editing"
              flexShrink={0}
              w="44px"
              h="44px"
              minW="44px"
              borderRadius="full"
              bg={ff.lime}
              color={ff.ink}
              _hover={{ opacity: 0.9 }}
              isLoading={isSavingCreatorLink}
              icon={<CheckIcon boxSize="18px" />}
              onClick={() => void handleSave()}
            />
          </Flex>
        ) : (
          <Flex
            h="60px"
            pl="16px"
            pr="8px"
            align="center"
            gap={2}
            bg="white"
            borderTopRadius="20px"
          >
            <Box
              as="button"
              type="button"
              flex={1}
              minW={0}
              display="flex"
              alignItems="center"
              gap={2}
              border="none"
              bg="transparent"
              p={0}
              m={0}
              textAlign="left"
              cursor={slugInputLooksLikePlaceholder ? 'default' : 'pointer'}
              disabled={slugInputLooksLikePlaceholder}
              aria-label={
                slugInputLooksLikePlaceholder
                  ? 'Set a creator link path to copy your profile URL'
                  : 'Copy creator link'
              }
              onClick={() => {
                if (slugInputLooksLikePlaceholder) return;
                onCopy();
              }}
              _hover={slugInputLooksLikePlaceholder ? undefined : { opacity: 0.85 }}
              _disabled={{ opacity: 1, cursor: 'default' }}
            >
              <Text
                as="span"
                fontSize="16px"
                lineHeight="24px"
                color={ff.muted}
                flexShrink={0}
                userSelect="none"
              >
                {BASE_HOST}
              </Text>
              <Text
                flex={1}
                minW={0}
                fontSize="16px"
                lineHeight="24px"
                fontWeight="400"
                color={slugInputLooksLikePlaceholder ? ff.faint : ff.ink}
                noOfLines={1}
              >
                {slugInputLooksLikePlaceholder ? 'yourname' : slug}
              </Text>
            </Box>
            {onSaveCreatorLink && (
              <IconButton
                type="button"
                aria-label="Edit creator link"
                flexShrink={0}
                w="44px"
                h="44px"
                minW="44px"
                variant="ghost"
                color={ff.ink}
                borderRadius="full"
                _hover={{ bg: ff.grey }}
                icon={<EditIcon boxSize="16px" />}
                onClick={() => {
                  setIsEditingSlug(true);
                }}
              />
            )}
          </Flex>
        )}

        <Button
          type="button"
          variant="unstyled"
          w="100%"
          display="block"
          textAlign="left"
          m={0}
          pl="16px"
          pr="16px"
          h="48px"
          minH="48px"
          borderTop="1px solid"
          borderColor={ff.line}
          onClick={onToggleWhere}
          _hover={{ bg: ff.grey }}
          borderRadius={0}
        >
          <HStack spacing="6px" align="center" justify="space-between" w="100%">
            <HStack spacing="6px" align="center" flex={1} minW={0}>
              <Icon
                as={ChevronDownIcon}
                w="16px"
                h="16px"
                color={ff.text2}
                transform={isWhereOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
                transition="transform 0.2s ease"
                flexShrink={0}
                aria-hidden
              />
              <Text as="span" fontSize="13px" lineHeight="18px" color={ff.text2} fontWeight={500}>
                How do I use this?
              </Text>
            </HStack>
          </HStack>
        </Button>

        <Collapse in={isWhereOpen} animateOpacity>
          <VStack
            align="flex-start"
            spacing={0}
            bg={ff.grey}
            borderTop="1px solid"
            borderColor={ff.line}
          >
            <Text fontSize="14px" lineHeight="1.5" color={ff.body} py="12px" px="16px" width="100%">
              Share this link with brands so they can purchase content directly from you — add it to your Instagram bio,
              DMs, or emails. We take <Text as="span" fontWeight={600} color={ff.ink}>0%</Text>.
            </Text>
            <Button
              type="button"
              w="100%"
              h="48px"
              borderRadius="0"
              fontFamily="display"
              fontSize="14px"
              fontWeight={600}
              color={ff.ink}
              leftIcon={<CopyIcon boxSize="16px" color={ff.ink} aria-hidden />}
              bg="white"
              borderTop="1px solid"
              borderColor={ff.line}
              onClick={onCopy}
              _hover={{ bg: ff.grey }}
            >
              Copy link
            </Button>
          </VStack>
        </Collapse>
      </Box>
    </Box>
  );
};

export default CreatorLinkSection;
